import React, { useState } from 'react';
import { Check, Copy, RefreshCw, Edit2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const VariantComparison = ({ 
  variants = [], 
  onSelect, 
  onRegenerate,
  selectedIndex = null,
  isLoading = false 
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedText, setEditedText] = useState('');

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    toast.success(`Variante ${index + 1} copiée !`);
  };

  const handleEdit = (index, text) => {
    setEditingIndex(index);
    setEditedText(text);
  };

  const handleSaveEdit = (index) => {
    // Update variant with edited text
    variants[index].text = editedText;
    setEditingIndex(null);
    toast.success('Modifications enregistrées');
  };

  const getToneColor = (tone) => {
    const colors = {
      formal: 'bg-blue-100 text-blue-800',
      casual: 'bg-green-100 text-green-800',
      professional: 'bg-purple-100 text-purple-800',
      engaging: 'bg-orange-100 text-orange-800',
      storytelling: 'bg-pink-100 text-pink-800'
    };
    return colors[tone] || 'bg-gray-100 text-gray-800';
  };

  if (variants.length === 0) {
    return (
      <div data-testid="variant-comparison-empty" className="text-center py-12 text-gray-500">
        <p>Aucune variante générée pour le moment.</p>
        <p className="text-sm mt-2">Cliquez sur "Générer avec IA" pour commencer.</p>
      </div>
    );
  }

  return (
    <div data-testid="variant-comparison" className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Variantes générées ({variants.length})</h3>
        <Button
          data-testid="regenerate-button"
          onClick={onRegenerate}
          disabled={isLoading}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Régénérer
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {variants.map((variant, index) => (
          <Card 
            key={index} 
            data-testid={`variant-card-${index}`}
            className={`relative transition-all ${selectedIndex === index ? 'ring-2 ring-primary' : ''}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">
                  Variante {index + 1}
                </CardTitle>
                <Badge className={getToneColor(variant.tone)}>
                  {variant.tone}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {editingIndex === index ? (
                <Textarea
                  data-testid={`variant-textarea-${index}`}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="min-h-[120px] text-sm"
                  placeholder="Éditez le texte..."
                />
              ) : (
                <p data-testid={`variant-text-${index}`} className="text-sm text-gray-700 whitespace-pre-wrap min-h-[120px]">
                  {variant.text}
                </p>
              )}

              {/* Hashtags */}
              {variant.hashtags && variant.hashtags.length > 0 && (
                <div data-testid={`variant-hashtags-${index}`} className="flex flex-wrap gap-1">
                  {variant.hashtags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="text-xs text-blue-600">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>{variant.char_count} caractères</span>
                <span>{variant.word_count} mots</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2">
                {editingIndex === index ? (
                  <>
                    <Button
                      data-testid={`save-edit-button-${index}`}
                      onClick={() => handleSaveEdit(index)}
                      size="sm"
                      className="flex-1"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Enregistrer
                    </Button>
                    <Button
                      data-testid={`cancel-edit-button-${index}`}
                      onClick={() => setEditingIndex(null)}
                      size="sm"
                      variant="outline"
                    >
                      Annuler
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      data-testid={`select-variant-button-${index}`}
                      onClick={() => onSelect(index)}
                      size="sm"
                      className="flex-1 gap-1"
                      variant={selectedIndex === index ? 'default' : 'outline'}
                    >
                      <Check className="h-4 w-4" />
                      {selectedIndex === index ? 'Sélectionnée' : 'Sélectionner'}
                    </Button>
                    <Button
                      data-testid={`edit-variant-button-${index}`}
                      onClick={() => handleEdit(index, variant.text)}
                      size="sm"
                      variant="ghost"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`copy-variant-button-${index}`}
                      onClick={() => handleCopy(variant.text, index)}
                      size="sm"
                      variant="ghost"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VariantComparison;
